/**
 * User Domain Service
 *
 * Domain service for user profile operations in the BiteWise healthcare provider platform.
 * Handles user profile fetching, updates, and related operations with enhanced error handling,
 * retry logic, and request deduplication.
 *
 * Migrated from userApi.ts to follow the new domain architecture pattern.
 *
 * @module services/domains/UserDomain
 */

import { BaseDomain, BaseApiError, ApiClient, tokenManager, requestManager, getLoggerWithPrefix, getLogger } from '../shared';
import type {
  UserMeResponse,
  UserProfile,
  UserProfileUpdateRequest,
  UserRole
} from '@/types/user';
import type {
  PatientProfileApiRequest,
  PatientProfileApiResponse
} from '@/types/patientRegistration';
import type {
  PatientReferralApiResponse,
  PatientReferralData,
  PatientDashboardDietician
} from '@/types/patientDashboard';

/**
 * Calendly calendar integration response
 */
export interface CalendlyCalendarResponse {
  scheduling_uri: string;
  user_uri: string;
  organization: string;
  name: string;
}

/**
 * User domain-specific error class
 */
export class UserApiError extends BaseApiError {
  /** The operation that failed */
  public readonly operation: string;

  constructor(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ) {
    super(message, status, fieldErrors, code);
    this.name = 'UserApiError';
    this.operation = operation;
  }
}

/**
 * User API endpoints
 */
const ENDPOINTS = {
  ME: '/users/me',
  UPDATE_PROFILE: '/users/me/patient-profile',
  PATIENT_PROFILE: '/users/me/patient-profile',
  CALENDLY_CALENDAR: '/users/me/calendar',
  PATIENT_ACTIVE_REFERRAL: '/referrals',
} as const;

/**
 * User Domain Service
 *
 * Provides user profile operations with enhanced error handling,
 * retry logic, and request deduplication.
 */
export class UserDomain extends BaseDomain<UserApiError> {
  constructor() {
    const apiClient = new ApiClient<UserApiError>(
      (message, status, fieldErrors, code) => new UserApiError('API_ERROR', message, status, fieldErrors, code)
    );

    super(apiClient, tokenManager, requestManager);
  }


  logger = getLoggerWithPrefix('UserDomain');

  /**
   * Get domain name for error messages
   */
  protected getDomainName(): string {
    return 'User';
  }

  /**
   * Create domain-specific error instance
   */
  protected createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): UserApiError {
    return new UserApiError(operation, message, status, fieldErrors, code);
  }

  /**
   * Get current user profile with retry and deduplication
   *
   * @returns Promise<UserProfile> Current user profile data
   * @throws UserApiError When request fails or user is not authenticated
   */
  async getCurrentUser(): Promise<UserProfile> {
    return this.executeWithDeduplication('getCurrentUser', async () => {
      return this.executeWithRetry(async () => {
        try {
          const response = await this.apiClient.get<UserMeResponse>(ENDPOINTS.ME);
          return this.transformUserResponse(response);
        } catch (error) {
          if (error instanceof UserApiError) {
            // Handle specific error cases with user-friendly messages
            if (error.status === 401) {
              throw this.createError(
                'getCurrentUser',
                'Authentication required. Please log in to continue.',
                401,
                undefined,
                'UNAUTHORIZED'
              );
            }
            if (error.status === 404) {
              throw this.createError(
                'getCurrentUser',
                'User profile not found. Please contact support.',
                404,
                undefined,
                'USER_NOT_FOUND'
              );
            }
          }
          throw error;
        }
      });
    });
  }

  /**
   * Update current user profile with enhanced error handling
   *
   * @param profileData Partial user profile data to update
   * @returns Promise<UserProfile> Updated user profile data
   * @throws UserApiError When request fails or validation errors occur
   */
  async updateProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
    return this.executeWithRetry(async () => {
      try {
        const requestData = this.transformUpdateRequest(profileData);
        const response = await this.apiClient.put<UserMeResponse>(
          ENDPOINTS.UPDATE_PROFILE,
          requestData
        );
        return this.transformUserResponse(response);
      } catch (error) {
        if (error instanceof UserApiError) {
          // Handle specific error cases with user-friendly messages
          if (error.status === 401) {
            throw this.createError(
              'updateProfile',
              'Authentication required. Please log in to continue.',
              401,
              undefined,
              'UNAUTHORIZED'
            );
          }
          if (error.status === 400) {
            throw this.createError(
              'updateProfile',
              'Invalid profile data. Please check your input and try again.',
              400,
              error.fieldErrors,
              'VALIDATION_ERROR'
            );
          }
          if (error.status === 404) {
            throw this.createError(
              'updateProfile',
              'User profile not found. Please contact support.',
              404,
              undefined,
              'USER_NOT_FOUND'
            );
          }
        }
        throw error;
      }
    });
  }

  /**
   * Refresh user profile data with retry mechanism
   *
   * @param maxRetries Maximum number of retry attempts (default: 3)
   * @returns Promise<UserProfile> Current user profile data
   * @throws UserApiError When all retry attempts fail
   */
  async refreshProfile(maxRetries: number = 3): Promise<UserProfile> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await this.getCurrentUser();
      } catch (error) {
        lastError = error as Error;

        // Don't retry on authentication errors
        if (error instanceof UserApiError && error.status === 401) {
          throw error;
        }

        // Wait before retrying (exponential backoff)
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    throw this.createError(
      'refreshProfile',
      `Failed to refresh profile after ${maxRetries} attempts: ${lastError?.message || 'Unknown error'}`,
      undefined,
      undefined,
      'REFRESH_FAILED'
    );
  }

  /**
   * Check if user has a specific role
   *
   * @param requiredRole Role to check for
   * @returns Promise<boolean> True if user has the required role
   */
  async hasRole(requiredRole: UserRole): Promise<boolean> {
    try {
      const profile = await this.getCurrentUser();
      return profile.role === requiredRole;
    } catch {
      // Return false if we can't determine the role
      return false;
    }
  }

  /**
   * Get Calendly calendar integration status
   *
   * @returns Promise<string> Calendly user's scheduling uri
   * @throws UserApiError When request fails or calendar integration not found
   */
  async getCalendlyCalendar(): Promise<string> {
    return this.executeWithDeduplication('getCalendlyCalendar', async () => {
      return this.executeWithRetry(async () => {
        try {
          const response = await this.apiClient.get<CalendlyCalendarResponse>(ENDPOINTS.CALENDLY_CALENDAR);
          return response.scheduling_uri;
        } catch (error) {
          if (error instanceof UserApiError) {
            // Handle specific error cases with user-friendly messages
            if (error.status === 401) {
              throw this.createError(
                'getCalendlyCalendar',
                'Authentication required. Please log in to continue.',
                401,
                undefined,
                'UNAUTHORIZED'
              );
            }
            if (error.status === 404) {
              throw this.createError(
                'getCalendlyCalendar',
                'Calendly integration not found. Please connect your Calendly account.',
                404,
                undefined,
                'CALENDLY_NOT_FOUND'
              );
            }
          }
          throw error;
        }
      });
    });
  }

  /**
   * Create or update patient profile
   *
   * @param profileData Patient profile form data
   * @returns Promise<PatientProfileApiResponse> Created/updated patient profile
   * @throws UserApiError When request fails or validation errors occur
   */
  async updatePatientProfile(apiData: PatientProfileApiRequest): Promise<PatientProfileApiResponse> {
    return this.executeWithRetry(async () => {
      try {
        const response = await this.apiClient.put<PatientProfileApiResponse>(
          ENDPOINTS.PATIENT_PROFILE,
          apiData
        );
        return response;
      } catch (error) {
        if (error instanceof UserApiError) {
          // Handle specific error cases with user-friendly messages
          if (error.status === 401) {
            throw this.createError(
              'updatePatientProfile',
              'Authentication required. Please log in to continue.',
              401,
              undefined,
              'UNAUTHORIZED'
            );
          }
          if (error.status === 400) {
            throw this.createError(
              'updatePatientProfile',
              'Invalid patient profile data. Please check your input and try again.',
              400,
              error.fieldErrors,
              'VALIDATION_ERROR'
            );
          }
          if (error.status === 404) {
            throw this.createError(
              'updatePatientProfile',
              'User not found. Please contact support.',
              404,
              undefined,
              'USER_NOT_FOUND'
            );
          }
        }
        throw error;
      }
    });
  }

  /**
   * Get patient profile
   *
   * @returns Promise<PatientProfileApiResponse> Current patient profile
   * @throws UserApiError When request fails or patient profile not found
   */
  async getPatientProfile(): Promise<PatientProfileApiResponse> {
    return this.executeWithDeduplication('getPatientProfile', async () => {
      return this.executeWithRetry(async () => {
        try {
          const response = await this.apiClient.get<PatientProfileApiResponse>(ENDPOINTS.PATIENT_PROFILE);
          return response;
        } catch (error) {
          if (error instanceof UserApiError) {
            // Handle specific error cases with user-friendly messages
            if (error.status === 401) {
              throw this.createError(
                'getPatientProfile',
                'Authentication required. Please log in to continue.',
                401,
                undefined,
                'UNAUTHORIZED'
              );
            }
            if (error.status === 404) {
              throw this.createError(
                'getPatientProfile',
                'Patient profile not found. Please complete your profile setup.',
                404,
                undefined,
                'PATIENT_PROFILE_NOT_FOUND'
              );
            }
          }
          throw error;
        }
      });
    });
  }

  /**
   * Get patient's active referral data
   *
   * @returns Promise<PatientReferralData | null> Active referral data or null if no active referral
   * @throws UserApiError When request fails or authentication required
   */
  async getPatientActiveReferral(): Promise<PatientReferralData | null> {
    return this.executeWithDeduplication('getPatientActiveReferral', async () => {
      return this.executeWithRetry(async () => {
        try {
          const response = await this.apiClient.get<PatientReferralApiResponse>(
            ENDPOINTS.PATIENT_ACTIVE_REFERRAL
          );

          // Transform API response to frontend format (snake_case to camelCase)
          return this.transformPatientReferralResponse(response);
        } catch (error) {
          if (error instanceof UserApiError) {
            // Handle specific error cases with user-friendly messages
            if (error.status === 401) {
              throw this.createError(
                'getPatientActiveReferral',
                'Authentication required. Please log in to continue.',
                401,
                undefined,
                'UNAUTHORIZED'
              );
            }
            if (error.status === 404) {
              // No active referral found - this is not an error, return null
              return null;
            }
            if (error.status === 403) {
              throw this.createError(
                'getPatientActiveReferral',
                'Access denied. Patient role required.',
                403,
                undefined,
                'FORBIDDEN'
              );
            }
          }
          throw error;
        }
      });
    });
  }

  /**
   * Transform API response to UserProfile (snake_case to camelCase)
   *
   * @private
   */
  private transformUserResponse(apiResponse: UserMeResponse): UserProfile {
    const baseProfile = {
      id: apiResponse.id,
      email: apiResponse.email,
      role: apiResponse.role,
      firstName: apiResponse.first_name,
      lastName: apiResponse.last_name,
      isActive: apiResponse.is_active,
      isVerified: apiResponse.is_verified,
      createdAt: apiResponse.created_at,
      updatedAt: apiResponse.updated_at,
    };

    // Add role-specific fields based on user role
    switch (apiResponse.role) {
      case 'provider':
        return {
          ...baseProfile,
          role: 'provider',
          providerNumber: apiResponse.provider_number,
          specialty: apiResponse.specialty,
          licenseNumber: apiResponse.license_number,
          practiceAddress: apiResponse.practice_address,
          phoneNumber: apiResponse.phone_number,
          profilePhotoUrl: apiResponse.profile_photo_url,
        };

      case 'dietician':
        return {
          ...baseProfile,
          role: 'dietician',
          licenseNumber: apiResponse.license_number,
          specializations: apiResponse.specializations,
          yearsOfExperience: apiResponse.years_of_experience,
          consultationRate: apiResponse.consultation_rate,
          availableHours: apiResponse.available_hours,
          profilePhotoUrl: apiResponse.profile_photo_url,
          bio: apiResponse.bio,
        };

      case 'patient':
        return {
          ...baseProfile,
          role: 'patient',
          dateOfBirth: apiResponse.date_of_birth,
          phoneNumber: apiResponse.phone_number,
          emergencyContact: apiResponse.emergency_contact,
          medicalConditions: apiResponse.medical_conditions,
          allergies: apiResponse.allergies,
          currentMedications: apiResponse.current_medications,
        };

      default:
        return baseProfile as UserProfile;
    }
  }

  /**
   * Transform UserProfile to API request format (camelCase to snake_case)
   *
   * @private
   */
  private transformUpdateRequest(profile: Partial<UserProfile>): UserProfileUpdateRequest {
    const request: UserProfileUpdateRequest = {};

    // Base fields
    if (profile.firstName !== undefined) request.first_name = profile.firstName;
    if (profile.lastName !== undefined) request.last_name = profile.lastName;

    // Role-specific fields
    if ('phoneNumber' in profile && profile.phoneNumber !== undefined) {
      request.phone_number = profile.phoneNumber;
    }
    if ('profilePhotoUrl' in profile && profile.profilePhotoUrl !== undefined) {
      request.profile_photo_url = profile.profilePhotoUrl;
    }

    // Provider-specific fields
    if ('providerNumber' in profile && profile.providerNumber !== undefined) {
      request.provider_number = profile.providerNumber;
    }
    if ('specialty' in profile && profile.specialty !== undefined) {
      request.specialty = profile.specialty;
    }
    if ('licenseNumber' in profile && profile.licenseNumber !== undefined) {
      request.license_number = profile.licenseNumber;
    }
    if ('practiceAddress' in profile && profile.practiceAddress !== undefined) {
      request.practice_address = profile.practiceAddress;
    }

    // Dietician-specific fields
    if ('specializations' in profile && profile.specializations !== undefined) {
      request.specializations = profile.specializations;
    }
    if ('yearsOfExperience' in profile && profile.yearsOfExperience !== undefined) {
      request.years_of_experience = profile.yearsOfExperience;
    }
    if ('consultationRate' in profile && profile.consultationRate !== undefined) {
      request.consultation_rate = profile.consultationRate;
    }
    if ('availableHours' in profile && profile.availableHours !== undefined) {
      request.available_hours = profile.availableHours;
    }
    if ('bio' in profile && profile.bio !== undefined) {
      request.bio = profile.bio;
    }

    // Patient-specific fields
    if ('dateOfBirth' in profile && profile.dateOfBirth !== undefined) {
      request.date_of_birth = profile.dateOfBirth;
    }
    if ('emergencyContact' in profile && profile.emergencyContact !== undefined) {
      request.emergency_contact = profile.emergencyContact;
    }
    if ('medicalConditions' in profile && profile.medicalConditions !== undefined) {
      request.medical_conditions = profile.medicalConditions;
    }
    if ('allergies' in profile && profile.allergies !== undefined) {
      request.allergies = profile.allergies;
    }
    if ('currentMedications' in profile && profile.currentMedications !== undefined) {
      request.current_medications = profile.currentMedications;
    }

    return request;
  }

  /**
   * Transform patient referral API response to frontend format
   * Converts snake_case to camelCase following BiteWise naming conventions
   *
   * @private
   */
  private transformPatientReferralResponse(apiResponse: PatientReferralApiResponse): PatientReferralData {
    return {
      id: apiResponse.id,
      referralToken: apiResponse.referral_token,
      referringPhysicianId: apiResponse.referring_physician_id,
      referringPhysicianName: apiResponse.referring_physician_name,
      referringPhysicianEmail: apiResponse.referring_physician_email,
      referringPhysicianRole: apiResponse.referring_physician_role,
      dietitianId: apiResponse.dietitian_id,
      dietitianName: apiResponse.dietitian_name,
      dietitianEmail: apiResponse.dietitian_email,
      dietitianSchedulingUri: apiResponse.dietitian_scheduling_uri,
      patientName: apiResponse.patient_name,
      dateOfBirth: apiResponse.date_of_birth,
      patientIdNumber: apiResponse.patient_id_number,
      patientIdType: apiResponse.patient_id_type,
      phone: apiResponse.phone,
      email: apiResponse.email,
      conditions: apiResponse.conditions,
      additionalNotes: apiResponse.additional_notes,
      status: apiResponse.status,
      claimedByUserId: apiResponse.claimed_by_user_id,
      claimedAt: apiResponse.claimed_at,
      expiresAt: apiResponse.expires_at,
      createdAt: apiResponse.created_at,
      updatedAt: apiResponse.updated_at,
    };
  }

  /**
   * Extract dietician information from referral data
   */
  static extractDieticianFromReferral(referralData: PatientReferralData): PatientDashboardDietician {
    return {
      id: referralData.dietitianId,
      name: referralData.dietitianName,
      email: referralData.dietitianEmail,
      schedulingUri: referralData.dietitianSchedulingUri,
    };
  }

  /**
   * Check if referral is expired
   */
  static isReferralExpired(referralData: PatientReferralData): boolean {
    const logger = getLogger();
    try {
      const expiryDate = new Date(referralData.expiresAt);
      const now = new Date();

      // Check for invalid dates
      if (isNaN(expiryDate.getTime())) {
        logger.warn('Invalid expiry date in referral data:', referralData.expiresAt);
        return false; // Treat invalid dates as non-expired for safety
      }

      return now > expiryDate && referralData.status === 'pending';
    } catch (error) {
      logger.error('Error checking referral expiry:', error);
      return false; // Default to non-expired on error
    }
  }

  /**
   * Calculate days until referral expires
   */
  static getDaysUntilExpiry(referralData: PatientReferralData): number | null {
    const logger = getLogger();
    try {
      const expiryDate = new Date(referralData.expiresAt);
      const now = new Date();

      // Check for invalid dates
      if (isNaN(expiryDate.getTime())) {
        logger.warn('Invalid expiry date in referral data:', referralData.expiresAt);
        return null; // Treat invalid dates as expired
      }

      const diffTime = expiryDate.getTime() - now.getTime();

      if (diffTime <= 0) {
        return null; // Already expired
      }

      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      logger.error('Error calculating days until expiry:', error);
      return null; // Default to expired on error
    }
  }

  /**
   * Check if patient can schedule appointments
   */
  static canScheduleAppointments(referralData: PatientReferralData): boolean {
    const logger = getLogger();
    try {
      const hasValidStatus = referralData.status !== 'cancelled';
      const isNotExpired = !this.isReferralExpired(referralData);
      const hasSchedulingUri = Boolean(referralData.dietitianSchedulingUri && referralData.dietitianSchedulingUri.length > 0);
      return hasValidStatus && isNotExpired && hasSchedulingUri;
    } catch (error) {
      logger.error('Error checking if can schedule appointments:', error);
      return false; // Default to cannot schedule on error
    }
  }
}

/**
 * Default user domain service instance
 */
export const userDomain = new UserDomain();

/**
 * Export types for external use
 */
export type {
  UserProfile,
  UserMeResponse,
  UserProfileUpdateRequest,
  UserRole,
  PatientProfileApiRequest,
  PatientProfileApiResponse
};